import { useAuthStore } from '@/stores/auth'
import { env, buildApiUrl, logger } from '@/config/env'

export interface ApiResponse<T = any> {
  data?: T
  error?: string
  status: number
}

export function useApi() {
  const authStore = useAuthStore()

  async function apiRequest<T = any>(
    endpoint: string,
    options: RequestInit = {},
  ): Promise<ApiResponse<T>> {
    try {
      // Build full URL
      const url = buildApiUrl(endpoint)
      
      logger.debug('API Request:', { endpoint, url, options })

      const authHeaders = authStore.getAuthHeaders()
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'x-api-key': env.API_KEY,
        ...authHeaders,
      }

      // Merge additional headers from options
      if (options.headers) {
        if (options.headers instanceof Headers) {
          options.headers.forEach((value, key) => {
            headers[key] = value
          })
        } else if (Array.isArray(options.headers)) {
          options.headers.forEach(([key, value]) => {
            headers[key] = value
          })
        } else {
          Object.assign(headers, options.headers)
        }
      }

      const response = await fetch(url, {
        ...options,
        headers,
      })

      const data = await response.json().catch(() => null)

      logger.debug('API Response:', { 
        url, 
        status: response.status, 
        data: env.DEBUG_MODE ? data : '[hidden]' 
      })

      if (!response.ok) {
        const errorMessage = data?.message || `HTTP ${response.status}: ${response.statusText}`
        logger.error('API Error:', { url, status: response.status, error: errorMessage })
        
        return {
          error: errorMessage,
          status: response.status,
        }
      }

      logger.info('API Success:', { url, status: response.status })
      
      return {
        data,
        status: response.status,
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Network error'
      logger.error('API Network Error:', { endpoint, error: errorMessage })
      
      return {
        error: errorMessage,
        status: 0,
      }
    }
  }

  async function get<T = any>(endpoint: string, options?: RequestInit): Promise<ApiResponse<T>> {
    return apiRequest<T>(endpoint, { ...options, method: 'GET' })
  }

  async function post<T = any>(
    endpoint: string,
    data?: any,
    options?: RequestInit,
  ): Promise<ApiResponse<T>> {
    return apiRequest<T>(endpoint, {
      ...options,
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    })
  }

  async function put<T = any>(
    endpoint: string,
    data?: any,
    options?: RequestInit,
  ): Promise<ApiResponse<T>> {
    return apiRequest<T>(endpoint, {
      ...options,
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    })
  }

  async function del<T = any>(endpoint: string, options?: RequestInit): Promise<ApiResponse<T>> {
    return apiRequest<T>(endpoint, { ...options, method: 'DELETE' })
  }

  return {
    apiRequest,
    get,
    post,
    put,
    delete: del,
  }
}
